import IInvoice from "~/interfaces/IInvoice"
import { CampProps, ReservationProps, SiteProps } from "~/interfaces"
import eSendingMode from "~/enums/eSendingMode"
import { LONG_TERM_RESERVATION_KINDS, longTermReservationKind, RESERVATION_KINDS } from "~/constants"
import { LINE_ITEM_KINDS } from "~/constants/line_item_kinds"
import { IFormData, ILineItemField } from "~/components/Admin/Invoices/EditInvoice"
import moment from "moment/moment"
import { cookieSendingModeInvoice } from "../constants"

const isDisplayingAsMonthly = (reservation: ReservationProps): boolean =>
  reservation ? moment(reservation.end_date).diff(moment(reservation.start_date), "days") >= 28 : false

const initializeFormDataFor = (
  invoice: IInvoice | undefined,
  site: SiteProps | undefined,
  camp: CampProps,
  isPreview: boolean,
  cookies?: any,
): IFormData => {
  const sendingModeFromCookie =
    cookies &&
    Object.values(eSendingMode)
      .filter((item) => item !== eSendingMode.NONE)
      .includes(cookies[cookieSendingModeInvoice])
      ? cookies[cookieSendingModeInvoice]
      : false

  let defaultSendingMode =
    sendingModeFromCookie || invoice?.reservation?.sending_mode || camp.booking_sending_mode || eSendingMode.EMAIL

  if (defaultSendingMode === "none") {
    defaultSendingMode = eSendingMode.EMAIL
  }

  const isReservationRecurring = invoice?.reservation?.kind === RESERVATION_KINDS.RECURRING
  const longTermKind = invoice?.reservation?.long_term_reservation?.kind

  let invoiceLineItems: ILineItemField[] | undefined = invoice?.order?.line_items?.map((item) => ({
    ...item,
    quantity: item.kind === LINE_ITEM_KINDS.ELECTRIC ? item.quantity : Math.round(item.quantity),
    unit_amount: item.unit_amount_in_cents / 100.0,
    total_amount: item.total_amount_in_cents / 100.0,
  }))

  if (isDisplayingAsMonthly(invoice?.reservation)) {
    invoiceLineItems = invoiceLineItems?.map((item) =>
      item.reservation_id
        ? {
            ...item,
            unit_amount: item.total_amount,
            is_hidden: longTermKind === LONG_TERM_RESERVATION_KINDS.UTILITIES_ONLY && isReservationRecurring,
          }
        : item,
    )
  }

  const hasElectricLineItem = invoiceLineItems?.some((item) => item.kind === LINE_ITEM_KINDS.ELECTRIC)

  const electricKinds: longTermReservationKind[] = [
    LONG_TERM_RESERVATION_KINDS.RENT_AND_UTILITIES,
    LONG_TERM_RESERVATION_KINDS.UTILITIES_ONLY,
  ]

  if (isReservationRecurring && longTermKind && electricKinds.includes(longTermKind) && !hasElectricLineItem) {
    const virtualElectricQuantity =
      (invoice?.reservation?.meter_reading &&
        invoice?.reservation?.last_meter_reading &&
        invoice?.reservation?.meter_reading - invoice?.reservation?.last_meter_reading) ||
      0
    const virtualElectricTotalAmount = virtualElectricQuantity * camp?.electricity_rate
    const newElectricLineItem: ILineItemField = {
      id: null,
      kind: LINE_ITEM_KINDS.ELECTRIC,
      label: "Electricity",
      unit_amount: camp.electricity_rate,
      quantity: virtualElectricQuantity,
      total_amount: virtualElectricTotalAmount,
      tax_rate: invoiceLineItems?.find((item) => item.tax_rate)?.tax_rate || 0,
      order_id: invoice?.order_id,
      add_on_id: null,
      reservation_id: null,
      parent_line_item_id: null,
      discount_id: null,
      occurrences: null,
      occurrence_unit: null,
      is_updating_series: false,
    }

    invoiceLineItems = [...(invoiceLineItems || []), newElectricLineItem]
  }

  return {
    electricity_fee: camp.electricity_rate,
    last_meter_reading: invoice?.reservation?.last_meter_reading || site?.last_meter_reading || 0,
    current_meter_reading: invoice?.reservation?.meter_reading || site?.last_meter_reading || 0,
    to_name: invoice?.order?.camper?.first_name + " " + invoice?.order?.camper?.last_name,
    to_email: invoice?.order?.camper?.email,
    to_phone: invoice?.order?.camper?.phone,
    to_address: invoice?.order?.camper?.address,
    memo: invoice?.memo || camp.default_invoice_memo || null,
    due_date: invoice?.due_date,
    items: invoiceLineItems?.map((item) => ({
      ...item,
      is_hidden: isPreview ? item.unit_amount == 0 : item.is_hidden,
    })),
    sending_mode: defaultSendingMode,
    is_displaying_occurrences: !isDisplayingAsMonthly(invoice?.reservation),
  }
}

export { initializeFormDataFor }
